import { z } from 'zod';

/**
 * Requirement #10 — Execution & Quality Intelligence Foundation.
 *
 * Canonical models normalize existing test_runs + quality_scenario_executions.
 * Does NOT replace those tables. Deterministic only — no AI.
 * ONE automation engine (mvp StepProcessor) remains the sole runner.
 *
 * QI-2 reliability lives in quality-execution-health.ts and extends the
 * summary response additively (see ScenarioExecutionSummaryWithReliabilitySchema).
 */

/** Deterministic failure classes for automation step/execution intelligence. */
export const EXECUTION_FAILURE_CLASSES = [
  'assertion_failure',
  'locator_not_found',
  'element_not_interactable',
  'timeout',
  'navigation_failure',
  'network_failure',
  'api_failure',
  'authentication_failure',
  'test_data_failure',
  'environment_failure',
  'browser_failure',
  'application_error',
  /** Wave 5A — visual mismatch / baseline pending; not auto product defect. */
  'visual_change',
  'unknown',
] as const;
export type ExecutionFailureClass = (typeof EXECUTION_FAILURE_CLASSES)[number];

export const AUTOMATION_STABILITY = ['stable', 'healed', 'retried', 'unstable'] as const;
export type AutomationStability = (typeof AUTOMATION_STABILITY)[number];

export const INTELLIGENCE_EXECUTION_MODES = [
  'interactive',
  'headless',
  'bulk',
  'ci',
  'schedule',
  'api',
  'manual',
] as const;
export type IntelligenceExecutionMode = (typeof INTELLIGENCE_EXECUTION_MODES)[number];

/** Future Shift-Right signal kinds — types only; not implemented in #10. */
export const FUTURE_PRODUCTION_SIGNAL_KINDS = [
  'production_error',
  'incident',
  'api_error',
  'performance_regression',
  'user_failure',
] as const;
export type FutureProductionSignalKind = (typeof FUTURE_PRODUCTION_SIGNAL_KINDS)[number];

export const FutureProductionSignalSchema = z.object({
  kind: z.enum(FUTURE_PRODUCTION_SIGNAL_KINDS),
  requirementId: z.string().uuid().nullable().optional(),
  scenarioId: z.string().uuid().nullable().optional(),
  automationFlowId: z.string().uuid().nullable().optional(),
  executionId: z.string().uuid().nullable().optional(),
  occurredAt: z.string().optional(),
  summary: z.string().max(2_000).optional(),
});
export type FutureProductionSignal = z.infer<typeof FutureProductionSignalSchema>;

export const StepHealingIntelligenceSchema = z.object({
  healingAttempted: z.boolean(),
  healingSuccessful: z.boolean(),
  originalLocator: z.string().nullable().optional(),
  healedLocator: z.string().nullable().optional(),
  healingStrategy: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  healingDurationMs: z.number().int().nonnegative().nullable().optional(),
  numberOfCandidates: z.number().int().nonnegative().nullable().optional(),
});
export type StepHealingIntelligence = z.infer<typeof StepHealingIntelligenceSchema>;

export const StepRetryIntelligenceSchema = z.object({
  retryAttempted: z.boolean(),
  retryCount: z.number().int().nonnegative(),
  finalAttempt: z.number().int().nonnegative(),
  retryReason: z.string().nullable().optional(),
});
export type StepRetryIntelligence = z.infer<typeof StepRetryIntelligenceSchema>;

export const ExecutionArtifactRefsSchema = z.object({
  screenshot: z.string().nullable().optional(),
  video: z.string().nullable().optional(),
  trace: z.string().nullable().optional(),
  logs: z.string().nullable().optional(),
  report: z.string().nullable().optional(),
});
export type ExecutionArtifactRefs = z.infer<typeof ExecutionArtifactRefsSchema>;

export const CanonicalStepResultSchema = z.object({
  stepId: z.string(),
  order: z.number().int().nonnegative(),
  keyword: z.string().optional(),
  action: z.string(),
  status: z.string(),
  duration: z.number().nonnegative(),
  expectedResult: z.string().nullable().optional(),
  actualResult: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  errorStack: z.string().nullable().optional(),
  errorType: z.enum(EXECUTION_FAILURE_CLASSES).nullable().optional(),
  artifacts: ExecutionArtifactRefsSchema.optional(),
  healing: StepHealingIntelligenceSchema.optional(),
  retry: StepRetryIntelligenceSchema.optional(),
});
export type CanonicalStepResult = z.infer<typeof CanonicalStepResultSchema>;

export const ExecutionMetricsSchema = z.object({
  totalSteps: z.number().int().nonnegative(),
  passedSteps: z.number().int().nonnegative(),
  failedSteps: z.number().int().nonnegative(),
  skippedSteps: z.number().int().nonnegative(),
  blockedSteps: z.number().int().nonnegative(),
  duration: z.number().nonnegative(),
  retryCount: z.number().int().nonnegative(),
  healedSteps: z.number().int().nonnegative(),
  failedAfterHealing: z.number().int().nonnegative(),
  failureCount: z.number().int().nonnegative(),
  automationStability: z.enum(AUTOMATION_STABILITY),
});
export type ExecutionMetrics = z.infer<typeof ExecutionMetricsSchema>;

export const QualityScoreDimensionsSchema = z.object({
  requirementCoverage: z.number().min(0).max(100).nullable(),
  automationCoverage: z.number().min(0).max(100).nullable(),
  executionPassRate: z.number().min(0).max(100).nullable(),
  executionStability: z.number().min(0).max(100).nullable(),
  healingRate: z.number().min(0).max(100).nullable(),
  failureRate: z.number().min(0).max(100).nullable(),
});
export type QualityScoreDimensions = z.infer<typeof QualityScoreDimensionsSchema>;

export const ExecutionTraceabilitySchema = z.object({
  requirementIds: z.array(z.string().uuid()),
  scenarioId: z.string().uuid(),
  automationFlowId: z.string().uuid().nullable(),
  executionId: z.string().uuid(),
  runId: z.string().uuid().nullable(),
  componentKeys: z.array(z.string()),
  /** Masked / token refs only — never resolved secret values. */
  testDataRefs: z.array(z.string()),
});
export type ExecutionTraceability = z.infer<typeof ExecutionTraceabilitySchema>;

export const CanonicalExecutionResultSchema = z.object({
  executionId: z.string().uuid(),
  scenarioId: z.string().uuid(),
  automationFlowId: z.string().uuid().nullable(),
  runId: z.string().uuid().nullable(),
  scenarioVersion: z.number().int().positive().nullable(),
  triggeredBy: z.string().uuid().nullable(),
  executionStatus: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  duration: z.number().nonnegative().nullable(),
  environment: z.string().nullable(),
  browser: z.string().nullable(),
  browserVersion: z.string().nullable(),
  executionMode: z.enum(INTELLIGENCE_EXECUTION_MODES),
  primaryFailureType: z.enum(EXECUTION_FAILURE_CLASSES).nullable(),
  metrics: ExecutionMetricsSchema,
  artifacts: ExecutionArtifactRefsSchema,
  steps: z.array(CanonicalStepResultSchema),
  traceability: ExecutionTraceabilitySchema,
  qualityScore: QualityScoreDimensionsSchema.optional(),
  enginePath: z.literal('step-processor').optional(),
});
export type CanonicalExecutionResult = z.infer<typeof CanonicalExecutionResultSchema>;

export const ScenarioExecutionHealthSchema = z.object({
  totalExecutions: z.number().int().nonnegative(),
  passRate: z.number().min(0).max(100).nullable(),
  failureRate: z.number().min(0).max(100).nullable(),
  healedExecutions: z.number().int().nonnegative(),
  retriedExecutions: z.number().int().nonnegative(),
  averageDurationMs: z.number().nonnegative().nullable(),
  requirementCoverage: z.number().min(0).max(100).nullable(),
  qualityScore: QualityScoreDimensionsSchema,
});
export type ScenarioExecutionHealth = z.infer<typeof ScenarioExecutionHealthSchema>;

export const ExecutionIntelligenceResponseSchema = z.object({
  intelligence: CanonicalExecutionResultSchema,
});
export type ExecutionIntelligenceResponse = z.infer<typeof ExecutionIntelligenceResponseSchema>;

export const ExecutionStepsIntelligenceResponseSchema = z.object({
  steps: z.array(CanonicalStepResultSchema),
  primaryFailureType: z.enum(EXECUTION_FAILURE_CLASSES).nullable(),
});
export type ExecutionStepsIntelligenceResponse = z.infer<typeof ExecutionStepsIntelligenceResponseSchema>;

export const ScenarioExecutionSummaryResponseSchema = z.object({
  scenarioId: z.string().uuid(),
  /**
   * Existing EI aggregate metrics (passRate / healedExecutions / qualityScore).
   * Not QI-2 reliability vocabulary; not Proof; not lifecycle health_status.
   */
  health: ScenarioExecutionHealthSchema,
  /** Machine-readable CI fields + optional evaluated gate (Requirement #11). */
  ci: z.object({
    status: z.string(),
    failureCount: z.number().int().nonnegative(),
    passRate: z.number().min(0).max(100).nullable(),
    metrics: ExecutionMetricsSchema.nullable(),
    gateVerdict: z.enum(['pass', 'warn', 'fail']).optional(),
    gateExitCode: z.union([z.literal(0), z.literal(1), z.literal(2)]).optional(),
  }),
});
export type ScenarioExecutionSummaryResponse = z.infer<typeof ScenarioExecutionSummaryResponseSchema>;

export const ScenarioExecutionHistoryFilterSchema = z.object({
  status: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  triggeredBy: z.string().uuid().optional(),
  browser: z.string().optional(),
  environment: z.string().optional(),
  failureType: z.enum(EXECUTION_FAILURE_CLASSES).optional(),
  healing: z.enum(['any', 'yes', 'no']).optional(),
  retry: z.enum(['any', 'yes', 'no']).optional(),
});
export type ScenarioExecutionHistoryFilter = z.infer<typeof ScenarioExecutionHistoryFilterSchema>;
